import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DonationSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setError('Payment status check timed out. Please check your email for confirmation or contact us.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/donations/checkout-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus(response.data);
        setLoading(false);
        return;
      } else if (response.data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setLoading(false);
        return;
      }

      // If payment is still pending, continue polling
      setPollAttempts(attempts + 1);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError('Error checking payment status. Please try again or contact us.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionId) {
      navigate('/donate');
      return;
    }

    // Start polling for payment status
    pollPaymentStatus(0);
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#e8f5f7] to-[#f0f8fa] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#14919f] mx-auto mb-4"></div>
          <p className="text-[#2c5570] text-lg font-semibold">Confirming your donation...</p>
          <p className="text-[#4a4a4a] text-sm mt-2">Attempt {pollAttempts + 1} of 10</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#e8f5f7] to-[#f0f8fa] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-[#2c5570] mb-4">Unable to Confirm Payment</h1>
          <p className="text-[#4a4a4a] mb-6">{error}</p>
          <Button 
            onClick={() => navigate('/donate')}
            className="bg-[#14919f] hover:bg-[#0f7280] text-white rounded-[50px] px-8"
          >
            Return to Donation Page
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e8f5f7] to-[#f0f8fa] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full">
        
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-[#2c5570] mb-2">Thank You for Your Generosity!</h1>
          <p className="text-lg text-[#14919f] font-semibold">Your donation has been processed successfully</p>
        </div>

        {/* Impact Summary */}
        <div className="bg-gradient-to-br from-[#e8f5f7] to-[#f0f8fa] rounded-lg p-6 mb-6">
          <div className="text-center">
            <p className="text-[#2c5570] text-5xl font-bold mb-2">
              {paymentStatus?.children_sponsored || 0}
            </p>
            <p className="text-[#2c5570] text-xl font-semibold mb-2">
              {paymentStatus?.children_sponsored === 1 ? 'Child' : 'Children'} Sponsored
            </p>
            <p className="text-[#4a4a4a] text-sm">
              That's {(paymentStatus?.children_sponsored || 0) * 10} books delivered to Delaware classrooms!
            </p>
          </div>
        </div>

        {/* Donation Details */}
        <div className="border-t border-b border-gray-200 py-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-[#4a4a4a]">Donation Amount:</span>
            <span className="text-[#2c5570] font-bold text-xl">
              ${paymentStatus?.amount_total?.toFixed(2) || '0.00'}
            </span>
          </div>
        </div>

        {/* Next Steps */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[#2c5570] mb-3">What Happens Next?</h3>
          <ul className="space-y-2 text-[#4a4a4a]">
            <li className="flex items-start">
              <span className="text-[#14919f] mr-2">✓</span>
              <span>A receipt has been sent to your email</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#14919f] mr-2">✓</span>
              <span>We'll keep you updated on our progress and the children you're helping</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#14919f] mr-2">✓</span>
              <span>Book distribution begins January 2027 with Delaware schools in highest need</span>
            </li>
          </ul>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button 
            onClick={() => navigate('/')}
            className="flex-1 bg-[#14919f] hover:bg-[#0f7280] text-white rounded-[50px] px-6 py-3"
          >
            Return to Homepage
          </Button>
          <Button 
            onClick={() => navigate('/about')}
            className="flex-1 bg-white hover:bg-gray-50 text-[#2c5570] border-2 border-[#2c5570] rounded-[50px] px-6 py-3"
          >
            Learn More About Us
          </Button>
        </div>

        {/* Additional Message */}
        <div className="mt-6 p-4 bg-[#fef3c7] rounded-lg">
          <p className="text-sm text-[#92400e] text-center">
            <strong>Thank you for believing in the power of books.</strong> Your generosity is changing lives in Delaware.
          </p>
        </div>
      </div>
    </div>
  );
}
